import styled, { css } from 'styled-components'
import React from 'react'
import getImage from '../../../utils/getOptimizedImage'
import sanitizeHtml from 'sanitize-html'
import PropTypes from 'prop-types'
import IconVerifiedCheckMark from '@/components/IconVerifiedCheckMark'
import Image from '@/components/Image'
import CardHeading from '@/components/CardHeading'
import CardLocation from '@/components/CardLocation'
import InsuranceAcceptedDisplay from '@/components/InsuranceAcceptedDisplay'
import PriceRangeDisplay from '@/components/PriceRangeDisplay'
import CardReviewsRating from '@/components/CardReviewsRating'
import CardShortDescription from '@/components/CardShortDescription'
import {
  computeAllReviewsCount,
  computeAllReviewsWeightedAverage
} from '../../../domain/centerReviews/centerReviews'
import getCenterDescription from '../../../domain/center/useCases/getCenterDescription'
import { shouldShowInsuranceTooltip } from '../../../domain/useCases/insurance/shouldShowInsuranceTooltip'
import { isNonEmptyArray, isNonEmptyString } from '../../../utils/checks'

const Style = styled.div`
  max-width: 70vw;
  backface-visibility: hidden;
  border-radius: 12px;
  cursor: pointer;
  position: relative;
  height: 100%;

  a {
    display: flex;
    flex-direction: column;
    height: 100%;
    text-decoration: none;
  }

  .image {
    position: relative;

    &::before {
      border: 1px solid rgba(0, 0, 0, 0.05);
      border-bottom: none;
      border-radius: 12px 12px 0 0;
      bottom: 0;
      content: '';
      display: block;
      left: 0;
      position: absolute;
      right: 0;
      top: 0;
    }

    img {
      aspect-ratio: 1.618 / 1;
      display: block;
      width: 100%;
      overflow: hidden;
      border-radius: 12px 12px 0 0;
    }
  }
  .blog-image {
    width: 100%;
    height: 200px;
    background: url(${(props) => props.blogImage});
    background-size: cover;
    background-position: center;
  }

  .ad {
    background-color: rgb(244, 244, 244);
    border-radius: 3rem;
    color: #555555;
    font-size: 12px;
    padding: 5px 10px;
    position: absolute;
    right: 16px;
    top: 16px;
  }

  .content {
    background-color: white;
    border: 1px solid #e1e7ef;
    border-top: none;
    border-radius: 0 0 12px 12px;
    flex: 1;
    padding: 16px;

    ${(props) =>
      props.borderless &&
      css`
        padding-bottom: 16px;
      `}

    a {
      color: ${(props) => props.theme.gray};
      text-decoration: none;
    }
  }

  .read-more {
    border-bottom: 2px solid black;
    padding-top: 5px;
    color: black !important;
    font-size: 16px;
    margin-top: 20px;
    font-weight: bold;
    border-bottom: 3px solid black;
    display: inline-block;
  }

  .title {
    display: -webkit-box;
    font-size: 20px;
    line-height: 24px;
    margin-top: 2px;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
  }

  .short-description {
    p {
      height: 60px;
      line-height: 20px;
      margin-top: 4px;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }
`

const NewsItem = ({
  blog,
  blogImage,
  borderless,
  hasCertifications,
  description,
  image,
  link,
  noReadMore,
  price,
  rawImage,
  sponsor,
  subtitle,
  title,
  shouldDefaultRegionalize = false,
  showAdBadge,
  overrideAdBadge = false,
  containerClassName,
  news,
  center,
  featuredTerm,
  hideDescription = false,
  isPopularTreatmentTypesCarousel = false,
  shouldOpenNewTabOnClick = true
}) => {
  const isAdBadgeVisible =
    typeof showAdBadge === 'undefined' ? sponsor : showAdBadge

  const removeTags = (str) => {
    if (str === null || str === '') return false
    else str = str.toString()
    return str.replace(/(<([^>]+)>)/gi, '')
  }

  const isInsuranceAccepted = shouldShowInsuranceTooltip({
    acceptedInsurance: center?.accepted_insurance,
    insuranceDescription: center?.insurance_description,
    verifyInsuranceLink: center?.verify_insurance_link
  })

  let imageSrc = null

  if (blogImage) imageSrc = blogImage

  if (image) {
    imageSrc =
      image?.includes('w_') && image?.includes('h_')
        ? image
        : image?.replace('/upload', '/upload/c_fill,w_306,h_200')
  }

  let shortDescription = description

  const centerDescription = getCenterDescription({
    center,
    termsList: [featuredTerm]
  })

  if (centerDescription) {
    shortDescription = centerDescription
  }

  if (blog) {
    shortDescription = removeTags(sanitizeHtml(description))
  }

  const allReviewsCount = computeAllReviewsCount(center)
  const allReviewsWeightedAverage = computeAllReviewsWeightedAverage(center)

  const shouldShowReviewsBadge =
    !!allReviewsCount || sponsor || hasCertifications || overrideAdBadge

  return (
    <Style
      className={`${containerClassName}`}
      blogImage={blogImage && (rawImage ? blogImage : getImage(blogImage, 300))}
      borderless={borderless}
    >
      <a
        target={shouldOpenNewTabOnClick ? '_blank' : '_self'}
        href={link}
      >
        {imageSrc && (
          <div className="relative">
            <div className="overflow-hidden rounded-t-xl">
              <Image
                aspectRatio="golden"
                loading="lazy"
                src={imageSrc}
              />
            </div>
            {isAdBadgeVisible && (
              <div className="absolute right-4 top-4 z-10 rounded-md bg-gray-100 px-2 py-0.5 text-xs text-gray-1100">
                Ad
              </div>
            )}
          </div>
        )}

        <div
          className={`flex-2 h-full rounded-xl rounded-t-none border border-t-0 border-gray-300 bg-white p-4 ${
            news ? 'rounded-t-xl !border-t' : ''
          }`}
        >
          <CardLocation
            location={subtitle}
            isPopularTreatmentTypesCarousel={isPopularTreatmentTypesCarousel}
          />

          <div
            className={`${isPopularTreatmentTypesCarousel ? 'h-1' : 'h-2'}`}
          ></div>
          <CardHeading tag="div">
            <div className="truncate font-FieldsVariable text-lg font-medium tracking-[0.2px]">
              {title}
            </div>
            {hasCertifications && (
              <div className="text-blue-400">
                <IconVerifiedCheckMark size="md" />
              </div>
            )}
          </CardHeading>

          {shouldShowReviewsBadge && (
            <div className="mt-1 flex h-6 items-center">
              <CardReviewsRating
                uniqueKey={link}
                rating={allReviewsWeightedAverage}
                isVerified={sponsor || hasCertifications}
                count={allReviewsCount}
                shouldDefaultRegionalize={shouldDefaultRegionalize}
                overrideVerified={overrideAdBadge}
              />
            </div>
          )}

          {(isNonEmptyString(price) || isInsuranceAccepted) && (
            <div className="mt-3">
              {isInsuranceAccepted && (
                <InsuranceAcceptedDisplay uniqueKey={link} />
              )}
              {isNonEmptyString(price) && !isInsuranceAccepted && (
                <PriceRangeDisplay priceRange={price} />
              )}
            </div>
          )}

          {!hideDescription && (
            <div
              className={`${
                isPopularTreatmentTypesCarousel ? 'pt-1.5' : 'pt-3'
              }`}
            >
              <CardShortDescription
                content={
                  shouldDefaultRegionalize
                    ? shortDescription
                        .replaceAll('center', 'centre')
                        .replaceAll('centers', 'centres')
                    : shortDescription
                }
                isPopularTreatmentTypesCarousel={
                  isPopularTreatmentTypesCarousel
                }
              />
            </div>
          )}
          {!noReadMore && (
            <span
              id="read-more"
              className="read-more"
            >
              Read More
            </span>
          )}
        </div>
      </a>
    </Style>
  )
}

export default NewsItem
